import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const SanBernardinoCountyPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?county=San Bernardino&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching San Bernardino properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>San Bernardino County Real Estate | Buy or Sell a Home | Truc Doan</title>
        <meta
          name="description"
          content="Looking to buy or sell a home in San Bernardino County, CA? Truc Doan is your trusted San Bernardino County real estate agent. Explore homes for sale in Ontario, Rancho Cucamonga, Fontana, Chino, Victorville, and more. Get a free home value estimate today."
        />
        <meta
          name="keywords"
          content="San Bernardino County real estate, homes for sale San Bernardino County CA, buy a home San Bernardino County, sell a home San Bernardino County, San Bernardino County realtor, Ontario homes for sale, Rancho Cucamonga real estate, Fontana homes for sale, Chino real estate, Chino Hills homes, Victorville real estate, Hesperia homes, Redlands real estate, Upland homes for sale, Rialto real estate, Colton homes, Yucaipa real estate, Big Bear homes, Truc Doan San Bernardino County, Inland Empire real estate agent, San Bernardino County home value"
        />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/san-bernardino-county`} />
        <meta property="og:title" content="San Bernardino County Real Estate | Buy or Sell a Home | Truc Doan" />
        <meta property="og:description" content="Your trusted San Bernardino County real estate expert. Browse homes for sale in Ontario, Rancho Cucamonga, Fontana, Chino Hills & more. Free home value estimates." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/san-bernardino-county`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="San Bernardino County, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/san-bernardino-county`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a top-rated real estate agent serving San Bernardino County, CA. Specializing in helping buyers find their dream home and sellers maximize their home value across all San Bernardino County cities.",
            "areaServed": {
              "@type": "AdministrativeArea",
              "name": "San Bernardino County, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            padding: '10px 20px',
            background: 'rgb(14, 165, 233)',
            color: 'rgb(243, 244, 246)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            zIndex: 10
          }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>{t('sb.h1')}</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            {t('sb.subtitle')}
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>{t('sb.whyTitle')}</h2>
          <p>{t('sb.whyP1')}</p>
          <p>{t('sb.whyP2')}</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>{t('county.buyingHome', { county: t('county.sanBernardinoCounty') })}</h2>
          <p>{t('sb.buyingP')}</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('sb.ranchoCucamonga')}</h3>
              <p>{t('sb.ranchoCucamongaDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('sb.chino')}</h3>
              <p>{t('sb.chinoDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('sb.ontario')}</h3>
              <p>{t('sb.ontarioDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('sb.fontana')}</h3>
              <p>{t('sb.fontanaDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('sb.victorville')}</h3>
              <p>{t('sb.victorvilleDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('sb.bigBear')}</h3>
              <p>{t('sb.bigBearDesc')}</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>{t('county.marketSnapshot', { county: t('county.sanBernardinoCounty') })}</h2>
          <p>{t('sb.marketP')}</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>{t('county.marketStat')}</th>
                <th>{t('county.value')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (San Bernardino County)</td>
                <td>~$520,000</td>
              </tr>
              <tr>
                <td>{t('county.avgDaysOnMarket')}</td>
                <td>30–50 Days</td>
              </tr>
              <tr>
                <td>{t('county.mostActivePriceRange')}</td>
                <td>$380K – $750K</td>
              </tr>
              <tr>
                <td>{t('county.topBuyerCities')}</td>
                <td>Rancho Cucamonga, Chino Hills, Ontario, Fontana</td>
              </tr>
              <tr>
                <td>{t('county.topSellerMarkets')}</td>
                <td>Rancho Cucamonga, Chino Hills, Upland</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="idxtown-vibe">
          <h2>{t('county.sellingHome', { county: t('county.sanBernardinoCounty') })}</h2>
          <p>{t('sb.sellingP1')}</p>
          <p dangerouslySetInnerHTML={{__html: t('sb.sellingP2')}} />
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('county.accuratePricing')}</h3>
              <p>{t('sb.pricingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.professionalMarketing')}</h3>
              <p>{t('sb.marketingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.expertNegotiation')}</h3>
              <p>{t('sb.negotiationDesc')}</p>
            </div>
          </div>
        </section>

        <section className="properties-section">
          <h2>{t('county.currentHomesForSale', { county: t('county.sanBernardinoCounty') })}</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available San Bernardino County listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>{t('county.readyToBuyOrSell', { county: t('county.sanBernardinoCounty') })}</h2>
          <p>{t('county.contactForConsultation')}</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=San Bernardino County Real Estate Inquiry`} className="cta-button primary">
              {t('county.emailAgent')}
            </a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">
              {t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              {t('county.searchCountyListings', { county: 'San Bernardino' })}
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Any amount of compensation or commission stated is made only to participants of CRMLS or an MLS that has entered into a datashare agreement with CRMLS.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default SanBernardinoCountyPage;
